import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, Input, LOCALE_ID, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Animations, ClickOutsideDirectiveModule, InputFieldModule, LoadingComponentModule, MotePipesModule, MotesLoginService, MotesToggleModule, SimpleSelectModule, SvgIconModule, TourSummaryModule } from '@motes-sdk';
import { Media, Mote, MotesPaths, PlanType, SubscriptionPlan, SubscriptionPlanMap, User } from '@motes-sdk/models';
import { AutoSub, AutoSubOnDestroy, ProjectService, ToastService, ToastType, UserService } from '@motes-sdk/providers';
import { ShownTourInfo } from '@motes-sdk/components/tour-summary/tour-summary.component';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { QrCodeModalComponent } from '../qr-code-modal/qr-code-modal.component';
import { PublishService } from '@app/services/publish.service';
import { CreatorTermsAcceptanceComponent } from '@app/components/creator-terms-acceptance/creator-terms-acceptance.component';
import { CreatorTermsAcceptanceContext, CreatorTermsService } from '@app/services/creator-terms.service';
import { SupabaseService } from '@motes-sdk/providers/supabase.service';
import { WebflowService } from '@app/services/webflow.service';

@AutoSubOnDestroy()
@Component({
    imports: [
        CommonModule,
        InputFieldModule,
        LoadingComponentModule,
        RouterModule,
        SimpleSelectModule,
        SvgIconModule,
        MotePipesModule,
        TourSummaryModule,
        MotesToggleModule,
        ClickOutsideDirectiveModule,
        QrCodeModalComponent,
        CreatorTermsAcceptanceComponent,
    ],
    standalone: true,
    selector: 'header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.fadeInOut(200, 0)]
})
export class HeaderComponent implements OnInit {

    @HostListener('document:mousedown', ['$event']) clickout(event) {
        if(this.showTestDropdown && !this.testDropdown?.nativeElement.contains(event.target)) {
            this.showTestDropdown = false;
        }

        if(this.showPublishDropdown && !this.publishDropdown?.nativeElement.contains(event.target)) {
            this.showPublishDropdown = false;
        }
    }

    @Input() editorMode: boolean = false;
    @Input() previewMode: boolean = false;

    public user: User;
    public activeTour: Mote;
    public activeStories: Mote[];
    public activeProjectKey: string = null;

    public showTestDropdown: boolean = false;
    public showPublishDropdown: boolean = false;
    public adminClicks: number = 0;
    public profileImage: Media = null;
    public plan: SubscriptionPlan;
    public tourCount: number = 0;
    public showSuperAdminToggle: boolean = false;
    public tourPreviewLink: string = 'storyhunt.dk';
    public tourShareLink: string = 'storyhunt.dk';

    public isQrCodeShown: boolean = false;
    public isPublishInProgress: boolean = false;
    public isChangePublished: boolean = false;
    public qrCodeUrl: string = '';
    public showPublishTermsModal: boolean = false;
    public publishTermsAgreed: boolean = false;
    public hasAcceptedPublishTerms: boolean = false;
    public hasPreviewPublishedOnce: boolean = false;
    private pendingPublishAction: 'live-changes' | 'live-qr' | 'preview-update' | 'preview-qr' | null = null;
    private pendingQrUrl: string = '';

    public readonly publishAcceptTitle = $localize`Content responsibility`;
    public readonly publishAcceptDescription = $localize`Before publishing, confirm that you are solely responsible for your tour content, including copyright and other rights. StoryHunt does not take responsibility for content you publish.`;

    public shownTourInfo: ShownTourInfo = {
        duration: true,
        length: true,
    };

    @ViewChild('testdropdown') testDropdown: ElementRef<HTMLDivElement>;
    @ViewChild('publishdropdown') publishDropdown: ElementRef<HTMLDivElement>;
    @ViewChild('previewTerms') previewTerms: CreatorTermsAcceptanceComponent;

    // Export to view
    public MotesPaths = MotesPaths;
    public LoginService = MotesLoginService;
    public PlanType = PlanType;

    constructor(
        private userService: UserService,
        private projectService: ProjectService,
        private router: Router,
        private route: ActivatedRoute,
        private publishService: PublishService,
        private creatorTerms: CreatorTermsService,
        private supabaseService: SupabaseService,
        private toastService: ToastService,
        private webflowService: WebflowService,
        @Inject(LOCALE_ID) public locale: string,
    ) { }

    ngOnInit(): void {
        AutoSub(this).reg['userSub'] = this.userService.watchCurrentUser().subscribe((user) => {
            this.user = user;
            this.profileImage = user?.profilePic;
            if(user?.isSuperAdmin) {
                this.showSuperAdminToggle = true;
            }
        });

        AutoSub(this).reg['publishTermsSub'] = this.userService.watchCurrentUser().pipe(
            switchMap((user) => {
                if(!user?.key) return of(false);
                return this.creatorTerms.watchHasAccepted('publishContentResponsibility', user.key);
            }),
        ).subscribe((accepted) => {
            this.hasAcceptedPublishTerms = accepted;
            this.publishTermsAgreed = accepted;
        });

        AutoSub(this).reg['tourSub'] = this.projectService.watchActiveTourDetails(true).subscribe(async (tourDetails) => {
            this.activeTour = tourDetails?.tour;
            this.activeStories = tourDetails?.children;

            this.tourPreviewLink = this.activeTour ? MotesPaths.GO_STORYHUNT_TOUR_URL(this.activeTour.key) : "";
            this.tourShareLink = await this.webflowService.getPublishedShareLink({
                tour: this.activeTour,
                translations: tourDetails?.translations,
                projectMotes: tourDetails?.projectMotes,
            });

            // Detect if changes in comparison to published tour version
            const lastSyncOn = await this.getTourSyncDate(this.activeTour?.key);
            this.hasPreviewPublishedOnce = lastSyncOn.getTime() > 0;
            this.isChangePublished = this.activeTour && (this.activeTour.updatedOn > lastSyncOn);
        });

        // Watch active project and watch project changes from database
        const projectUpdates = this.projectService.watchActiveProject().pipe(switchMap(ev => {
            if(!ev.project) return of(null);
            return this.projectService.watchProject(ev.project.key);
        }));

        AutoSub(this).reg['projectSub'] = projectUpdates.subscribe((project) => {
            this.activeProjectKey = project?.key || null;
            // Get subscription plan from project
            this.plan = SubscriptionPlanMap[ project?.creatorPlan || PlanType.PERSONAL];
        });

        AutoSub(this).reg['tourCountSub'] = this.projectService.watchPublishedTourCount().subscribe( tourCount => {
            this.tourCount = tourCount;
        });

        AutoSub(this).reg['queryParamsSub'] = this.route.queryParams.subscribe((ev) => {
            if (ev?.popup == 'publish') {
                this.showPublishDropdown = true;
            }
        })
    }

    public get publishTermsContext(): CreatorTermsAcceptanceContext {
        const source = this.pendingPublishAction === 'live-qr'
            ? 'live-qr'
            : (this.showPublishTermsModal || this.pendingPublishAction === 'live-changes')
                ? 'publish-changes'
                : 'preview';
        return {
            projectKey: this.activeProjectKey || this.activeTour?.project,
            projectTitle: this.projectService.activeProject?.title || '',
            tourKey: this.activeTour?.key,
            tourTitle: this.activeTour?.title,
            source,
        };
    }

    private async getTourSyncDate(tourKey: string) : Promise<Date> {
        // Get supabase client
        const db = await this.supabaseService.getSupabaseClient();
        // Select last updated on date from Supabase tour
        const { data:syncTour, error } = await db.from('tours').select('updatedOn').eq('key', tourKey).maybeSingle();
        if(error || !syncTour?.updatedOn) return new Date(0);
        // Return date of last sync
        return new Date(syncTour.updatedOn);
    }

    public async goToDashboard() {
        // Navigate to local storage project, first of user's projects or 'no worlds' dashboard page
        let latestProject = this.user ? await this.projectService.getLatestUserProject(this.user.key) : null;
        if(latestProject) return this.router.navigate(['dashboard', latestProject]);
        return this.router.navigateByUrl('dashboard');
    }

    public async goToTourDashboard(tour: Mote) {
        if(!tour) return;
        return this.router.navigate(['dashboard', tour.project, 'tours', tour.key]);
    }

    public onAdminToggleClick() {
        this.adminClicks += 1;
        // Init SuperAdmin mode (enabled)
        if(this.user?.isSuperAdmin && this.adminClicks == 5) {
            this.LoginService.superAdminModeEnabled = true;
            this.showSuperAdminToggle = true;
        }
    }

    public async showQrCodeModal(url: string, options?: { forcePtgPreview?: boolean }) {
        if(options?.forcePtgPreview) {
            const ready = await this.ensurePublishTermsAccepted('preview-qr');
            if(!ready) return;
            this.isQrCodeShown = true;
            this.qrCodeUrl = url;
            await this.proceedPreviewPublish('');
            return;
        }

        this.pendingQrUrl = url;
        const ready = await this.ensurePublishTermsAccepted('live-qr');
        if(!ready) return;
        await this.proceedLiveQr(url);
    }

    public async publishTour() {
        this.showPublishDropdown = false;
        this.router.navigate(['editor', this.activeProjectKey, this.activeTour.key, 'publish']);
    }

    public async unpublishTour() {
        this.isPublishInProgress = true;
        // Immediately decrement tour count to avoid short flickering limit reached msg
        this.tourCount = this.tourCount -1;

        // Unpublish tour
        this.publishService.unpublishTour(this.activeTour, this.activeStories);

        this.isPublishInProgress = false;
    }

    public async publishChanges() {
        if(this.isPublishInProgress) return;

        const ready = await this.ensurePublishTermsAccepted('live-changes');
        if(!ready) return;
        await this.proceedPublishChanges();
    }

    public async onPublishTermsConfirmed() {
        const action = this.pendingPublishAction;
        this.pendingPublishAction = null;
        this.hasAcceptedPublishTerms = true;
        this.publishTermsAgreed = true;
        await this.runPendingPublishAction(action);
    }

    public cancelPublishTerms() {
        this.showPublishTermsModal = false;
        this.pendingPublishAction = null;
        this.pendingQrUrl = '';
    }

    private async ensurePublishTermsAccepted(action: 'live-changes' | 'live-qr' | 'preview-update' | 'preview-qr'): Promise<boolean> {
        if(action === 'live-changes' || action === 'live-qr') {
            if(this.hasAcceptedPublishTerms) {
                this.publishTermsAgreed = true;
                return true;
            }
            this.pendingPublishAction = action;
            this.showPublishTermsModal = true;
            this.showPublishDropdown = false;
            return false;
        }

        if(this.hasAcceptedPublishTerms) return true;

        if(!this.publishTermsAgreed) {
            this.toastService.addToast($localize`Please accept the content responsibility terms first.`, ToastType.ERR);
            return false;
        }

        const ok = await this.previewTerms?.ensureAccepted({
            ...this.publishTermsContext,
            source: 'preview',
        });
        if(ok) {
            this.hasAcceptedPublishTerms = true;
            this.publishTermsAgreed = true;
        }
        return Boolean(ok);
    }

    private async runPendingPublishAction(action: 'live-changes' | 'live-qr' | 'preview-update' | 'preview-qr' | null) {
        if(action === 'live-changes') await this.proceedPublishChanges();
        if(action === 'live-qr') await this.proceedLiveQr(this.pendingQrUrl || this.tourShareLink);
        if(action === 'preview-update') await this.proceedPreviewPublish($localize`Preview successfully updated`);
        if(action === 'preview-qr') {
            this.isQrCodeShown = true;
            this.qrCodeUrl = this.tourPreviewLink;
            await this.proceedPreviewPublish('');
        }
    }

    private async proceedLiveQr(url: string) {
        this.pendingQrUrl = '';
        this.isQrCodeShown = true;
        this.qrCodeUrl = url;
        void this.publishService.publishChanges(this.activeTour, this.activeStories, '');
    }

    private async proceedPublishChanges() {
        if(this.isPublishInProgress) return;
        this.isPublishInProgress = true;
        try {
            const toastLabel = $localize`Changes successfully published`;
            await this.publishService.publishChanges(this.activeTour, this.activeStories, toastLabel);
        } finally {
            this.isPublishInProgress = false;
        }
    }

    private async proceedPreviewPublish(toastLabel: string) {
        if(this.isPublishInProgress) return;
        this.isPublishInProgress = true;
        try {
            await this.publishService.publishChanges(
                this.activeTour,
                this.activeStories,
                toastLabel,
                { forcePtgPreview: true },
            );
            this.hasPreviewPublishedOnce = true;
        } finally {
            this.isPublishInProgress = false;
        }
    }

    public async publishPreviewChanges() {
        const ready = await this.ensurePublishTermsAccepted('preview-update');
        if(!ready) return;
        const previewStatus = this.hasPreviewPublishedOnce
            ? $localize`Preview successfully updated`
            : $localize`Preview created successfully`;
        await this.proceedPreviewPublish(previewStatus);
    }

    public async onPreviewLinkCopied() {
        if (!this.activeTour?.key || !this.hasAcceptedPublishTerms || !this.hasPreviewPublishedOnce) return;
        this.toastService.addToast($localize`Link copied to clipboard – updating link...`, ToastType.OK);
        const ok = await this.publishService.pushPreviewToPtgOnly(this.activeTour.key);
        if (ok) {
            this.toastService.addToast(
                $localize`Preview link updated successfully`,
                ToastType.OK,
            );
        }
        // On failure, pushPreviewToPtgOnly already shows an error toast.
    }

    public openContactEmail() {
        window.open(`mailto: ${MotesPaths.YASSIN_EMAIL}`);
    }
}
